import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
  Divider,
} from '@mui/material';
import { ArrowLeft, Plus, FileText, Eye, Send, Pencil } from 'lucide-react';
import { templatesApi } from '../../../api/templates.api';
import useUIStore from '../../../store/useUIStore';

const TemplatesPage = ({
  sess = { uid: '', role: 'user' },
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [submittingId, setSubmittingId] = useState(null);
  const rowsPerPage = 10;
  const showSnackbar = useUIStore((state) => state.showSnackbar);
  const openConfirmDialog = useUIStore((state) => state.openConfirmDialog);
  const cardSx = {
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    boxShadow: 'none',
  };
  const primaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: `0 10px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
  };
  const secondaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    borderColor: alpha(theme.palette.primary.main, 0.25),
    '&:hover': {
      borderColor: alpha(theme.palette.primary.main, 0.45),
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
  };
  const tableContainerSx = {
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
  };

  const getApiErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    return (
      data?.error?.error_user_msg ||
      data?.error?.message ||
      data?.message ||
      data?.error ||
      error?.message ||
      fallback
    );
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await templatesApi.getAll({ uid: sess.uid });
      const result = response?.data?.data || response?.data || [];
      setTemplates(Array.isArray(result) ? result : []);
      setFilteredTemplates(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    applyFilters();
  }, [searchQuery, statusFilter, categoryFilter, templates]);

  const applyFilters = () => {
    let filtered = [...templates];
    if (searchQuery.trim()) {
      const search = searchQuery.toLowerCase();
      filtered = filtered.filter((tpl) =>
        (tpl.t_tname || tpl.name || '').toLowerCase().includes(search) ||
        (tpl.t_type || tpl.category || '').toLowerCase().includes(search) ||
        (tpl.t_language || tpl.language || '').toLowerCase().includes(search)
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter((tpl) => {
        const status = (tpl.t_status || tpl.status || '').toString().toLowerCase();
        return status === statusFilter.toLowerCase();
      });
    }
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((tpl) => {
        const category = (tpl.t_type || tpl.category || '').toString().toLowerCase();
        return category === categoryFilter.toLowerCase();
      });
    }
    setFilteredTemplates(filtered);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredTemplates.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredTemplates.slice(startIndex, startIndex + rowsPerPage);

  const statusChip = (status) => {
    const normalized = (status || '').toString().toLowerCase();
    const map = {
      approved: { label: 'Approved', color: 'success' },
      pending: { label: 'Pending', color: 'warning' },
      submitted: { label: 'Submitted', color: 'info' },
      created: { label: 'Created', color: 'default' },
      rejected: { label: 'Rejected', color: 'error' },
      failed: { label: 'Failed', color: 'error' },
      draft: { label: 'Draft', color: 'default' },
    };
    const conf = map[normalized] || map.pending;
    return <Chip label={conf.label} color={conf.color} size="small" variant="outlined" />;
  };

  const normalizeHeaderHandle = (value) => {
    const raw = `${value || ''}`.trim();
    if (!raw) return '';
    if (/^(https?:)?\/\//i.test(raw) || /^blob:|^data:/i.test(raw)) return '';
    return raw;
  };

  const resolveHeaderHandle = (detail = {}, headerComponent = {}) =>
    normalizeHeaderHandle(
      detail.headerHandle ||
        detail.header_handle ||
        detail.file ||
        detail.header?.example?.header_handle?.[0] ||
        headerComponent.example?.header_handle?.[0] ||
        detail.header?.header_handle ||
        detail.t_file
    );

  const extractNumericVariables = (text = '') => {
    const matches = `${text}`.match(/\{\{(\d+)\}\}/g) || [];
    const numbers = matches.map((m) => m.replace(/[{}]/g, '')).filter(Boolean);
    return Array.from(new Set(numbers)).sort((a, b) => Number(a) - Number(b));
  };

  const getBodyTextFromDetail = (detail = {}) =>
    detail?.body?.text ||
    detail?.body?.body ||
    detail?.t_btext ||
    detail?.bodyText ||
    '';

  const buildVariableExampleMap = (detail = {}) => {
    const map = {};
    const variables = Array.isArray(detail.variables) ? detail.variables : [];
    variables.forEach((v) => {
      const key = `${v?.tag || v?.tagNumber || ''}`.trim();
      if (key) map[key] = v?.example || v?.value || v?.sample || '';
    });
    const tags = Array.isArray(detail.tags) ? detail.tags : [];
    tags.forEach((t) => {
      const key = `${t?.tagNumber || t?.tag || ''}`.trim();
      if (key && !map[key]) map[key] = t?.value || t?.example || '';
    });
    return map;
  };

  const ensureBodyExample = (component, detail = {}) => {
    const bodyText = component?.text || getBodyTextFromDetail(detail);
    const vars = extractNumericVariables(bodyText);
    if (!vars.length) return component;
    if (component?.example?.body_text?.[0]?.length) return component;
    const valueMap = buildVariableExampleMap(detail);
    const sampleArray = vars.map((v) => valueMap[v] || `sample_${v}`);
    return {
      ...component,
      text: bodyText || component?.text,
      example: { ...(component.example || {}), body_text: [sampleArray] },
    };
  };

  const sanitizeComponentsForSubmit = (components = [], detail = {}) =>
    components
      .map((component) => {
        if (!component) return component;
        if (!component.content) return component;
        const merged = { ...component.content, ...component };
        delete merged.content;
        delete merged.id;
        delete merged.order;
        delete merged.createdAt;
        delete merged.updatedAt;
        return merged;
      })
      .filter(Boolean)
      .map((component) => {
        const type = String(component.type || '').toUpperCase();
        if (type === 'HEADER') {
          const { mediaUrl, media_url, url, file, ...rest } = component;
          const format = String(rest.format || rest.header_format || rest.media_type || '').toUpperCase();
          if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(format)) {
            const currentHandle = normalizeHeaderHandle(rest.example?.header_handle?.[0]);
            const resolvedHandle = currentHandle || resolveHeaderHandle(detail, rest);
            if (resolvedHandle) {
              rest.example = { ...(rest.example || {}), header_handle: [resolvedHandle] };
            } else if (rest.example?.header_handle) {
              const { header_handle, ...exampleRest } = rest.example || {};
              rest.example = Object.keys(exampleRest).length ? exampleRest : undefined;
            }
          }
          return rest;
        }
        if (type === 'BODY') {
          const bodyText = component?.text || getBodyTextFromDetail(detail);
          return ensureBodyExample({ ...component, text: bodyText || component?.text }, detail);
        }
        if (type === 'BUTTONS' && Array.isArray(component.buttons)) {
          return {
            ...component,
            buttons: component.buttons.map((btn) => {
              const { value, phoneNumber, urlVariable, urlType, ...rest } = btn || {};
              return rest;
            }),
          };
        }
        return component;
      });

  const handleSubmitTemplate = async (template) => {
    const templateId = template?.t_id || template?.id;
    if (!templateId) return;
    setSubmittingId(templateId);
    try {
      const detailResponse = await templatesApi.getById(templateId);
      const detail = detailResponse?.data?.data || detailResponse?.data || {};
      const name = detail.t_tname || detail.name || template?.t_tname || template?.name || '';
      const category = detail.t_type || detail.category || template?.t_type || template?.category || '';
      const language = detail.t_lang || detail.language || template?.t_language || template?.language || '';
      const rawComponents = Array.isArray(detail.components)
        ? detail.components
        : Array.isArray(detail.preview?.components)
          ? detail.preview.components
          : [];
      let components = sanitizeComponentsForSubmit(rawComponents, detail);
      if (!components.some((component) => String(component?.type || '').toUpperCase() === 'BODY')) {
        const bodyText = getBodyTextFromDetail(detail);
        if (bodyText) {
          components = [
            ...components,
            ensureBodyExample({ type: 'BODY', text: bodyText }, detail),
          ];
        }
      }
      const missingMediaHandle = components.some((component) => {
        const type = String(component.type || '').toUpperCase();
        if (type !== 'HEADER') return false;
        const format = String(component.format || component.header_format || component.media_type || '').toUpperCase();
        if (!['IMAGE', 'VIDEO', 'DOCUMENT'].includes(format)) return false;
        return !component.example?.header_handle?.[0];
      });

      if (missingMediaHandle) {
        showSnackbar({
          message: 'Media header handle is missing or invalid. Edit the template and re-upload the media.',
          severity: 'error',
        });
        return;
      }
      if (components.length && name && category && language) {
        const updateResponse = await templatesApi.update(templateId, { name, category, language, components });
        const updateData = updateResponse?.data || {};
        if (updateData?.success === false || updateData?.error) {
          const message = getApiErrorMessage({ response: { data: updateData } }, 'Failed to update template');
          showSnackbar({ message, severity: 'error' });
          return;
        }
      }
      const submitResponse = await templatesApi.submit(templateId);
      const submitData = submitResponse?.data || {};
      if (submitData?.success === false || submitData?.error) {
        const message = getApiErrorMessage({ response: { data: submitData } }, 'Failed to submit template');
        showSnackbar({ message, severity: 'error' });
        return;
      }
      showSnackbar({ message: 'Template submitted for approval', severity: 'success' });
      setTemplates((prev) =>
        prev.map((item) =>
          (item.t_id || item.id) === templateId
            ? { ...item, t_status: 'SUBMITTED', status: 'SUBMITTED' }
            : item
        )
      );
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to submit template');
      console.error('Failed to submit template:', error);
      showSnackbar({ message, severity: 'error' });
    } finally {
      setSubmittingId(null);
    }
  };

  const handleConfirmSubmit = (template) => {
    const name = template?.t_tname || template?.name || 'this template';
    openConfirmDialog({
      title: 'Submit for approval',
      message: `Submit "${name}" for approval?`,
      onConfirm: () => handleSubmitTemplate(template),
    });
  };

  return (
    <Box sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.02), minHeight: '100%' }}>
      <Card sx={{ ...cardSx, mb: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)` }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" startIcon={<ArrowLeft size={18} />} href="/" sx={secondaryButtonSx}>
                Back
              </Button>
              <Box>
                <Typography variant="h5" fontWeight={700}>Templates</Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage, review, and submit templates
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={() => navigate('/templates/create')}
              sx={primaryButtonSx}
            >
              Create Template
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ ...cardSx, mb: 2 }}>
        <CardContent>
          <Divider sx={{ mb: 2, opacity: 0.6 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FileText size={16} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Select
                fullWidth
                size="small"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="created">Created</MenuItem>
                <MenuItem value="submitted">Submitted</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} md={4}>
              <Select
                fullWidth
                size="small"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="utility">Utility</MenuItem>
                <MenuItem value="marketing">Marketing</MenuItem>
                <MenuItem value="otp">OTP</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={cardSx}>
        <CardContent>
          <TableContainer sx={tableContainerSx}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                  <TableCell>S.No</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, rowIndex) => (
                    <TableRow key={`skeleton-${rowIndex}`}>
                      <TableCell><Skeleton variant="text" width={24} /></TableCell>
                      <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="40%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="40%" /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={80} height={24} /></TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Skeleton variant="circular" width={28} height={28} />
                          <Skeleton variant="circular" width={28} height={28} />
                          <Skeleton variant="circular" width={28} height={28} />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : currentData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography align="center" color="text.secondary">
                        No templates found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  currentData.map((tpl, index) => (
                    <TableRow
                      key={tpl.t_id || tpl.id || index}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
                      }}
                    >
                      <TableCell>{startIndex + index + 1}</TableCell>
                      <TableCell>{tpl.t_tname || tpl.name || '-'}</TableCell>
                      <TableCell>{tpl.t_language || tpl.language || '-'}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{tpl.t_type || tpl.category || '-'}</TableCell>
                      <TableCell>{statusChip(tpl.t_status || tpl.status)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton size="small" color="primary" onClick={() => navigate(`/templates/${tpl.t_id || tpl.id}`)}>
                            <Eye size={16} />
                          </IconButton>
                          {['created', 'draft'].includes(String(tpl.t_status || tpl.status || '').toLowerCase()) && (
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => navigate(`/templates/edit/${tpl.t_id || tpl.id}`)}
                            >
                              <Pencil size={16} />
                            </IconButton>
                          )}
                          {String(tpl.t_status || tpl.status || '').toLowerCase() === 'created' && (
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleConfirmSubmit(tpl)}
                              disabled={submittingId === (tpl.t_id || tpl.id)}
                            >
                              <Send size={16} />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={2}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Typography variant="body2">Page {currentPage} of {totalPages}</Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TemplatesPage;
